import React, { useMemo } from 'react';
import { useMetrics, MetricsRequest } from '../../api/metrics';
import { LoadingState } from '../../components/common/LoadingState';
import { LineChart } from '@plerionhq/design-system/experimental';

import { useTranslation } from 'react-i18next';
import { formatLocale } from '@plerionhq/design-system';

interface PRStatisticsChartProps {
  sourceId?: string;
  days?: number;
}

export const PRStatisticsChart: React.FC<PRStatisticsChartProps> = ({
  sourceId,
  days = 30,
}) => {
  const { t } = useTranslation('pleri/pages');
  // Build metrics request for PR statistics
  const metricsRequest = useMemo<MetricsRequest | null>(() => {
    if (!sourceId) {
      return null;
    }

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    // Calculate interval in seconds (1 day = 86400 seconds)
    const interval = 86400;

    return {
      metric: {
        namespace: 'pleri_code_fix',
        metricNames: [
          `change_request_created#source#${sourceId}`,
          `change_request_completed#source#${sourceId}`,
          `change_request_closed#source#${sourceId}`,
        ],
      },
      period: {
        interval,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        stat: 'sum',
      },
    };
  }, [sourceId, days]);

  const {
    data: metricsResponse,
    isLoading,
    error,
  } = useMetrics(metricsRequest, {
    enabled: !!sourceId,
  });

  // Process metrics data to format for chart
  // The series names are the row fields and the legend labels, so both
  // come from the same strings.
  const seriesNames = useMemo(
    () => ({
      created: t('created'),
      merged: t('merged'),
      closed: t('closedRejected'),
    }),
    [t],
  );
  const chartData = useMemo(() => {
    if (!metricsResponse?.data) {
      return [];
    }

    const { period, metrics } = metricsResponse.data;

    // Create a map to store data by timestamp
    const dataByDate = new Map<
      string,
      {
        timestamp: string;
        date: string;
        [series: string]: number | string;
      }
    >();

    // Initialize all dates with zero values
    period.timestamps.forEach((timestamp: string) => {
      const date = new Date(timestamp).toLocaleDateString(formatLocale(), {
        month: 'short',
        day: 'numeric',
      });
      dataByDate.set(timestamp, {
        timestamp,
        date,
        [seriesNames.created]: 0,
        [seriesNames.merged]: 0,
        [seriesNames.closed]: 0,
      });
    });

    // Process each metric and populate the data
    metrics.forEach((metric: { metricName: string; values: number[] }) => {
      const metricName = metric.metricName;

      // Map metric names to chart series
      let seriesKey: string | undefined;
      if (metricName.includes('change_request_created')) {
        seriesKey = seriesNames.created;
      } else if (metricName.includes('change_request_completed')) {
        seriesKey = seriesNames.merged;
      } else if (metricName.includes('change_request_closed')) {
        seriesKey = seriesNames.closed;
      }

      if (seriesKey) {
        // Match values with timestamps
        metric.values.forEach((value, index) => {
          const timestamp = period.timestamps[index];
          const dataPoint = dataByDate.get(timestamp);
          if (dataPoint) {
            dataPoint[seriesKey] = value;
          }
        });
      }
    });

    // Convert to array, sort by timestamp, and remove timestamp from output
    return Array.from(dataByDate.values())
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      )
      .map(({ timestamp: _timestamp, ...rest }) => rest);
  }, [metricsResponse, seriesNames]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <LoadingState text={t('loadingPrStatistics')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-700 dark:bg-red-950">
        <p>
          {t('errorLoadingPrStatistics', {
            message: (error as Error)?.message || t('unknownError'),
          })}
        </p>
      </div>
    );
  }

  if (chartData.length === 0 && !isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('prStatistics')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('numberOfPullRequestsCreatedMergedAnd')}
          </p>
        </div>
        <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
          <p>{t('noPrStatisticsDataAvailableForThis')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('prStatistics')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('numberOfPullRequestsCreatedMergedAnd')}
        </p>
      </div>
      <LineChart
        data={chartData}
        series={[seriesNames.created, seriesNames.merged, seriesNames.closed]}
        categoryKey="date"
        showLegend={true}
        className="h-64"
        colors={['#21883D', '#8251DF', '#CF232E']}
      />
    </div>
  );
};
